import * as React from 'react';
import { useContext, useState } from 'react';
import {
  Button as MUIButton,
  Stack,
  CircularProgress,
} from '@mui/material';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm } from 'react-hook-form';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

function generatePassword(length = 8) {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*?';
  const all = upper + lower + digits + special;

  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];

  for (let i = 4; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password.split('').sort(() => 0.5 - Math.random()).join('');
}

export default function CreatePartnerUser() {
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(generatePassword());

  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const regeneratePassword = () => {
    const newPass = generatePassword();
    setTempPassword(newPass);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        password: tempPassword,
        isChangePassword: 0, // Force user to change password on first login
      };

      console.log('Creating partner user with payload:', { ...payload, password: '***hidden***' });

      const response = await postRequest('private/partner-user-register', payload, authCtx.token);

      if (response?.status === 201) {
        toast.success('User created successfully! Login credentials sent to email.');
        reset();
        setTempPassword(generatePassword());
        navigate('/users'); // Or wherever you list partner users
      } else {
        toast.error(response?.message || 'User creation failed');
      }
    } catch (err) {
      toast.error(err?.message || 'User creation failed');
      console.error('User creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Users', path: '/users' },
          { label: 'Add User', path: '' },
        ]}
      />
      <br />

      <div className='page-header'>
        <h1 className='mt-0'>Add New User</h1>
        <p className='text-muted'>Create a new user for your organization</p>
      </div>

      <div className='page-top-formview mb-3'>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* First Name */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="forename">
              <Form.Label>First Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter first name"
                {...register('forename', { required: 'First name is required' })}
                isInvalid={!!errors.forename}
              />
              <Form.Control.Feedback type="invalid">
                {errors.forename?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Last Name */}
            <Form.Group as={Col} controlId="surname">
              <Form.Label>Last Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter last name"
                {...register('surname', { required: 'Last name is required' })}
                isInvalid={!!errors.surname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.surname?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Username */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="username">
              <Form.Label>Username *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores',
                  },
                })}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Email */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Phone Number */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="phone_number">
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                {...register('phone_number', {
                  required: 'Phone number is required',
                })}
                isInvalid={!!errors.phone_number}
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone_number?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Auto-Generated Password */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="password">
              <Form.Label>Auto-Generated Password</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Form.Control
                  type="text"
                  value={tempPassword}
                  readOnly
                  style={{
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                  }}
                />
                <MUIButton
                  variant="outlined"
                  onClick={regeneratePassword}
                  type="button"
                  size="small"
                >
                  Regenerate
                </MUIButton>
              </div>
              <Form.Text className="text-muted">
                This password will be sent to the user's email. They will be required to change it on first login.
              </Form.Text>
            </Form.Group>
          </Row>

          {/* Submit Buttons */}
          <Stack spacing={2} direction="row" className="mt-4">
            <MUIButton
              variant="contained"
              type="submit"
              className='red-bg red-button text-capitalize'
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Creating User...' : 'Create User'}
            </MUIButton>
            <MUIButton
              variant="outlined"
              onClick={() => navigate('/users')}
              disabled={loading}
              className='text-capitalize'
            >
              Cancel
            </MUIButton>
          </Stack>
        </Form>
      </div>
    </>
  );
}
